// The field's own runtime, and only for the role that works in the field
// (D-83, D-172).
//
// Five things run on their own while an operator works: the engine that sends
// the queue, the queue of photos, the agenda that reads itself again, the
// request for persistent storage and the keeping of the browser's offer to
// install. All five are the field's. A session of the office or of the portal
// starts none of them: nothing of theirs is queued, no agenda is theirs to
// read, and a device they sign in on is not a working tablet whose storage
// must survive or whose home screen needs the application.
//
// B-50 (D-435): the offer itself is heard from the page's first moment, for
// whoever signs in, because Chrome makes it before anybody has; heard is not
// kept. Only this runtime takes it from the browser and makes an invitation
// of it, so the office and the portal see what the browser shows them, as
// they always did (app/install.ts).
//
// The role is followed, not read once. The same tab can end one person's
// session and open another's; when the field's session goes, what runs for
// the field stops with it, and a session of another role that follows starts
// nothing again.
import type { SessionManager, SessionSnapshot } from '../session/manager';
import type { AgendaRefresher } from '../planning/refresh';
import type { SyncEngine } from '../sync/engine';
import type { PhotoQueue } from '../sync/photoQueue';
import type { InstallService } from './install';

/** The role that works in the field. Every other role waits (D-172). */
export const FIELD_ROLE = 'operations';

/** Whether the session is one the field's runtime runs for: a profile of the
 *  field's role, whatever state the session is in. A revoked, suspended or
 *  expired session of an operator keeps its runtime as it always did — each
 *  piece already refuses to send without a session — so for operations
 *  nothing changes; only a profile of another role, or none, stops it. */
export function runsTheField(snapshot: SessionSnapshot): boolean {
  return snapshot.profile?.role === FIELD_ROLE;
}

export interface FieldRuntimeParts {
  session: SessionManager;
  engine: SyncEngine;
  photos: PhotoQueue;
  agenda: AgendaRefresher;
  install: InstallService;
  /** Ask the browser to keep the local stores under pressure; asked once. */
  persistStorage: () => Promise<void>;
}

export class FieldRuntime {
  private readonly parts: FieldRuntimeParts;
  private running = false;
  private unsubscribeSession: (() => void) | null = null;
  private unsubscribeEngine: (() => void) | null = null;

  constructor(parts: FieldRuntimeParts) {
    this.parts = parts;
  }

  /** Follow the session from now on, and start at once if it is the field's. */
  start(): void {
    this.unsubscribeSession ??= this.parts.session.subscribe(() => {
      this.follow();
    });
    this.follow();
  }

  /** Stop following the session and stop whatever runs. */
  stop(): void {
    this.unsubscribeSession?.();
    this.unsubscribeSession = null;
    this.halt();
  }

  /** Whether the field's runtime is running now. */
  isRunning(): boolean {
    return this.running;
  }

  private follow(): void {
    const field = runsTheField(this.parts.session.getSnapshot());
    if (field && !this.running) this.begin();
    else if (!field && this.running) this.halt();
  }

  private begin(): void {
    const { engine, photos, agenda, install, persistStorage } = this.parts;
    this.running = true;
    // INC-05b: every settled data cycle may have made photos eligible, their
    // visit or the revision that declares them now known to the server.
    this.unsubscribeEngine = engine.subscribe((state) => {
      if (!state.running) photos.trigger();
    });
    void install.start();
    engine.start();
    photos.start();
    // D-225: sending what waits and bringing back what changed are the two
    // halves of the same thing, and both belong to the application rather
    // than to a screen: the agenda is read again when the application opens
    // with signal, every fifteen minutes, and when the signal comes back.
    // B-73 (D-483): and with it, the forms that changed.
    agenda.start();
    void persistStorage();
  }

  private halt(): void {
    if (!this.running) return;
    const { engine, photos, agenda, install } = this.parts;
    this.running = false;
    this.unsubscribeEngine?.();
    this.unsubscribeEngine = null;
    engine.stop();
    photos.stop();
    agenda.stop();
    install.stop();
  }
}
